/**
 * Issue Builders — Factory functions, signature hashing, deduplication, penalty math.
 *
 * Split from issueContract.ts (C1, Sprint 5). This is the SSOT for:
 *  - blocker() / coach() / info() factories
 *  - createIssueSignature() + djb2Hash (deterministic, C18)
 *  - deduplicateIssues() (signature-based, tracks occurrenceCount per C19)
 *  - computeIssuePenalty() (shared by gate + orchestrator domain scoring)
 *
 * Design principles:
 * - N5: Issue determinism — same input → same signature → same dedup outcome
 */

import type { IssueCode, IssueDomain, PipelineStage } from './codes';
import type { ValidationIssue } from './types';
import { DEFAULT_SCORING_WEIGHTS, type ScoringWeights } from '../severityScale';

// ─── Signature Hashing ────────────────────────────────────────────────────────

/**
 * djb2 hash — deterministic, fast, no collisions in practice for short strings.
 * Used by createIssueSignature() (C18, Sprint 1) so we hash the full normalized
 * context instead of truncating at 100 chars. Truncation caused multiple distinct
 * long-explanation issues to collide into one signature and be dedup'd away.
 */
function djb2Hash(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    // hash * 33 + char
    hash = (hash << 5) + hash + input.charCodeAt(i) | 0;
  }
  // Convert to unsigned and base36 for compactness
  return (hash >>> 0).toString(36);
}

/**
 * Create a deterministic signature for an issue.
 * Ensures N5: same input → same issues.
 *
 * C18 (Sprint 1): full context is hashed (djb2) instead of truncated at 100 chars,
 * so two long but distinct contexts no longer collapse into one signature.
 */
export function createIssueSignature(domain: IssueDomain, code: IssueCode, field?: string, context?: string): string {
  const normalizedContext = (context ?? '').trim().toLowerCase();
  const contextHash = normalizedContext ? djb2Hash(normalizedContext) : '';
  return `${domain}::${code}::${field ?? ''}::${contextHash}`;
}

// ─── Issue Construction Helpers ───────────────────────────────────────────────

/**
 * Create a blocker issue (blocks production + assessment).
 *
 * R2 (Sprint 5+): `metadata` is an optional structured payload for issues
 * that carry actionable machine-readable data (e.g. SEED_DOK_MISSING folds
 * the dokAutoTagger heuristic estimate into metadata so the admin UI can
 * offer a one-click "apply suggestion" action without a separate issue).
 */
export function blocker(domain: IssueDomain, stage: PipelineStage, code: IssueCode, message: string, opts?: {
  field?: string;
  expected?: string;
  actual?: string;
  suggestion?: string;
  questionId?: string;
  familyId?: string;
  severity?: number;
  metadata?: Record<string, unknown>;
  /** GSR-19: optional display-layer sub-stage tag (e.g. '5b-cognitive'). */
  subStage?: string;
}): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'blocker',
    domain,
    message,
    stage,
    subStage: opts?.subStage,
    severity: opts?.severity ?? 80,
    field: opts?.field,
    expected: opts?.expected,
    actual: opts?.actual,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
    metadata: opts?.metadata
  };
}

/**
 * Create a coach issue (quality improvement suggestion, may trigger review).
 *
 * R2 (Sprint 5+): `metadata` is an optional structured payload (see blocker()).
 */
export function coach(domain: IssueDomain, stage: PipelineStage, code: IssueCode, message: string, opts?: {
  field?: string;
  expected?: string;
  actual?: string;
  suggestion?: string;
  questionId?: string;
  familyId?: string;
  severity?: number;
  metadata?: Record<string, unknown>;
  /** GSR-19: optional display-layer sub-stage tag (e.g. '5b-cognitive'). */
  subStage?: string;
}): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'coach',
    domain,
    message,
    stage,
    subStage: opts?.subStage,
    severity: opts?.severity ?? 45,
    field: opts?.field,
    expected: opts?.expected,
    actual: opts?.actual,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
    metadata: opts?.metadata
  };
}

/**
 * Create an info issue (informational, no gate impact).
 *
 * R2 (Sprint 5+): `metadata` is an optional structured payload. Producers
 * may attach machine-readable data (e.g. dokAutoTagger output) so admin UI
 * can render actionable suggestions without re-running heuristics.
 */
export function info(domain: IssueDomain, stage: PipelineStage, code: IssueCode, message: string, opts?: {
  field?: string;
  suggestion?: string;
  questionId?: string;
  familyId?: string;
  severity?: number;
  metadata?: Record<string, unknown>;
  /** GSR-19: optional display-layer sub-stage tag (e.g. '5b-cognitive'). */
  subStage?: string;
}): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'info',
    domain,
    message,
    stage,
    subStage: opts?.subStage,
    severity: opts?.severity ?? 10,
    field: opts?.field,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
    metadata: opts?.metadata
  };
}

// ─── Penalty Math ─────────────────────────────────────────────────────────────

/**
 * Compute total penalty for a set of issues.
 * Shared by evaluateGate (gate/evaluate.ts) and computeDomainScores (orchestrator).
 *
 * Phase 4: weights are now configurable via the second parameter, which
 * defaults to DEFAULT_SCORING_WEIGHTS (= the exact historical values
 * blocker=30, coach=severity/4, info=2). Callers SHOULD pass the live
 * policy weights via `getScoringWeights()` so admin-tuned overrides take
 * effect; the default keeps back-compat for any caller that hasn't migrated.
 */
export function computeIssuePenalty(issues: ValidationIssue[], weights: ScoringWeights = DEFAULT_SCORING_WEIGHTS): number {
  return issues.reduce((sum, issue) => {
    switch (issue.kind) {
      case 'blocker':
        return sum + weights.blockerPenalty;
      case 'coach':
        return sum + Math.round(issue.severity / weights.coachPenaltyDivisor);
      case 'info':
        return sum + weights.infoPenalty;
      default:
        return sum;
    }
  }, 0);
}

// ─── Deduplication ────────────────────────────────────────────────────────────

/**
 * Deduplicate issues by signature.
 * When duplicates exist, keep the highest-severity one.
 *
 * C19 (Sprint 1): the kept issue carries an `occurrenceCount` reflecting how
 * many times that signature appeared in the input. This lets the admin UI
 * surface family-wide problems as "(×5)" without inflating the issue list.
 */
export function deduplicateIssues(issues: ValidationIssue[]): ValidationIssue[] {
  const seen = new Map<string, ValidationIssue>();
  const counts = new Map<string, number>();
  for (const issue of issues) {
    counts.set(issue.signature, (counts.get(issue.signature) ?? 0) + 1);
    const existing = seen.get(issue.signature);
    if (!existing || issue.severity > existing.severity) {
      seen.set(issue.signature, issue);
    }
  }

  // Stamp occurrenceCount on the winners (only when > 1 to keep payloads slim)
  return Array.from(seen.values()).map(issue => {
    const count = counts.get(issue.signature) ?? 1;
    return count > 1 ? {
      ...issue,
      occurrenceCount: count
    } : issue;
  });
}